import React from "react";
import { BringToFrontIcon, CopyIcon, CrosshairIcon, MinusIcon, PlusIcon, SendToBackIcon, Trash2Icon } from "lucide-react";
import { fonts, inkColors } from "../../data/designAssets";
import { sizeLimits } from "../design/DesignElementView";
import { isToneOnTone } from "../../utils/markingEffect";
import type { Design } from "../../hooks/useDesign";
import type { DesignElement, MarkingTechnique } from "../../types/configurator";
interface ElementInspectorProps {
  el: DesignElement;
  design: Design;
  marking: MarkingTechnique | null;
  logoColors: number;
  onLogoColors: (n: number) => void;
  onSelect: (id: string | null) => void;
}
export function ElementInspector({
  el,
  design,
  marking,
  logoColors,
  onLogoColors,
  onSelect
}: ElementInspectorProps) {
  const [min, max] = sizeLimits(el.kind);
  const toneOnTone = isToneOnTone(marking?.id ?? null);
  const iconBtn = "flex h-9 w-9 items-center justify-center border border-ink/20 transition-colors duration-150 ease-out hover:border-ink";
  const actions = [{
    label: "Centrer",
    icon: CrosshairIcon,
    run: () => design.updateCommit(el.id, {
      x: 0.5,
      y: 0.5
    })
  }, {
    label: "Dupliquer",
    icon: CopyIcon,
    run: () => onSelect(design.duplicate(el.id))
  }, {
    label: "Premier plan",
    icon: BringToFrontIcon,
    run: () => design.reorder(el.id, "front")
  }, {
    label: "Arrière-plan",
    icon: SendToBackIcon,
    run: () => design.reorder(el.id, "back")
  }];
  return <div className="space-y-5" data-id="element-278" data-mp="2anapqtvcar11">
      <div className="flex flex-wrap items-center gap-1.5" data-id="element-279" data-mp="2aq6lqcnmn5mu">
        {actions.map(a => {
        const Icon = a.icon;
        return <button key={a.label} type="button" onClick={a.run} aria-label={a.label} title={a.label} className={iconBtn} data-id="element-280" data-mp="2kn1leyvmzc2p">
              <Icon size={16} aria-hidden="true" data-id="element-281" data-mp="2pkkz9xof9mu4" />
            </button>;
      })}
        <button type="button" onClick={() => {
        design.remove(el.id);
        onSelect(null);
      }} className="ml-auto flex h-9 items-center gap-2 px-3 text-sm font-bold text-cherry transition-colors duration-150 ease-out hover:bg-cherry hover:text-paper" data-id="element-282" data-mp="q9abrzdb4s79">
          <Trash2Icon size={16} aria-hidden="true" data-id="element-283" data-mp="i9460wjfk6kn" />
          Supprimer
        </button>
      </div>

      {el.kind === "text" && <>
          <div data-id="element-284" data-mp="s4xnmuc0aq9a">
            <label htmlFor="el-text" className="text-xs font-bold" data-id="element-285" data-mp="s4fhn5zjkxo6">
              Texte
            </label>
            <input id="el-text" value={el.text} maxLength={40} onFocus={design.beginGesture} onBlur={design.endGesture} onChange={e => design.updateLive(el.id, {
          text: e.target.value
        })} className="mt-2 w-full border border-ink px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-mint" data-id="element-286" data-mp="163xco33fl75z" />
          </div>
          <div data-id="element-287" data-mp="1ovzjynu6nb3k">
            <p className="text-xs font-bold" data-id="element-288" data-mp="1otddysmxx3ng">Police</p>
            <div className="mt-2 grid grid-cols-3 gap-1.5" data-id="element-289" data-mp="1kv60q7roqkh0">
              {fonts.map(f => <button key={f.id} type="button" aria-pressed={el.fontId === f.id} onClick={() => design.updateCommit(el.id, {
            fontId: f.id
          })} className={`truncate border px-2 py-2 text-sm transition-colors duration-150 ease-out ${el.fontId === f.id ? "border-ink bg-ink text-paper" : "border-ink/20 hover:border-ink"}`} style={{
            fontFamily: f.family,
            fontWeight: f.weight
          }} data-id="element-290" data-mp="23jhui2ufezxw">
                  {f.name}
                </button>)}
            </div>
          </div>
        </>}

      {el.kind !== "image" && <div data-id="element-291" data-mp="1ftvwhipsiqrh">
          <p className="text-xs font-bold" data-id="element-292" data-mp="1ftnexdiq2x9y">Couleur d'encre</p>
          {toneOnTone ? <p className="mt-2 text-sm text-muted" data-id="element-293" data-mp="11u4dt4obwm75">{marking?.name} : rendu ton sur ton, sans encre.</p> : <div className="mt-2 flex flex-wrap gap-2" data-id="element-294" data-mp="16rmigivbcf9c">
              {inkColors.map(c => {
          const selected = el.color.toLowerCase() === c.hex.toLowerCase();
          return <button key={c.id} type="button" aria-label={c.name} title={c.name} aria-pressed={selected} onClick={() => design.updateCommit(el.id, {
            color: c.hex
          })} className={`h-8 w-8 rounded-full border border-ink/30 ${selected ? "ring-2 ring-ink ring-offset-2" : ""}`} style={{
            backgroundColor: c.hex
          }} data-id="element-295" data-mp="254s5pz3lrf66" />;
        })}
            </div>}
        </div>}

      {el.kind === "image" && marking && marking.maxColors > 1 && <div data-id="element-296" data-mp="3bsyta6uhelo4">
          <p className="text-xs font-bold" data-id="element-297" data-mp="3bsp3aj9fseif">Couleurs dans votre logo</p>
          <div className="mt-2 flex items-center gap-3" data-id="element-298" data-mp="36tcdpqr6ktc5">
            <div className="flex items-stretch border border-ink" data-id="element-299" data-mp="36tvq6ry2apis">
              <button type="button" aria-label="Moins de couleurs" disabled={logoColors <= 1} onClick={() => onLogoColors(logoColors - 1)} className="flex h-9 w-9 items-center justify-center border-r border-ink hover:bg-mist disabled:text-muted/40" data-id="element-300" data-mp="3edn0vw837smm">
                <MinusIcon size={14} aria-hidden="true" data-id="element-301" data-mp="3t3u97j4drv9l" />
              </button>
              <span className="flex w-10 items-center justify-center text-sm font-bold tabular-nums" data-id="element-302" data-mp="2vfk8r8k8tfkl">{logoColors}</span>
              <button type="button" aria-label="Plus de couleurs" disabled={logoColors >= marking.maxColors} onClick={() => onLogoColors(logoColors + 1)} className="flex h-9 w-9 items-center justify-center border-l border-ink hover:bg-mist disabled:text-muted/40" data-id="element-303" data-mp="30f6mdj3zgxtx">
                <PlusIcon size={14} aria-hidden="true" data-id="element-304" data-mp="omhzfmpk5s9f" />
              </button>
            </div>
            <span className="text-xs text-muted" data-id="element-305" data-mp="2ln4fwskthkd0">Impacte le prix en {marking.name.toLowerCase()}</span>
          </div>
        </div>}

      <Slider id="el-size" label="Taille" value={el.size} min={min} max={max} step={el.kind === "text" ? 1 : 0.01} display={`${Math.round((el.size - min) / (max - min) * 100)} %`} design={design} onChange={v => design.updateLive(el.id, {
      size: v
    })} data-id="element-306" data-mp="2gr6wuei65zwj" />
      <Slider id="el-rotation" label="Rotation" value={el.rotation} min={-180} max={180} step={1} display={`${Math.round(el.rotation)}°`} design={design} onChange={v => design.updateLive(el.id, {
      rotation: v
    })} data-id="element-307" data-mp="1xz4pjtrf3vyy" />
    </div>;
}
interface SliderProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  design: Design;
  onChange: (v: number) => void;
}
function Slider({
  id,
  label,
  value,
  min,
  max,
  step,
  display,
  design,
  onChange
}: SliderProps) {
  return <div data-id="element-308" data-mp="3uoxmz4hfq6hu">
      <div className="flex items-center justify-between" data-id="element-309" data-mp="3uppiygey268n">
        <label htmlFor={id} className="text-xs font-bold" data-id="element-310" data-mp="3gx9ct6qqxs3i">
          {label}
        </label>
        <span className="text-xs tabular-nums text-muted" data-id="element-311" data-mp="3gvg47lwkoxsv">{display}</span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onFocus={design.beginGesture} onBlur={design.endGesture} onChange={e => onChange(Number(e.target.value))} className="mt-2 w-full accent-ink" data-id="element-312" data-mp="3lu9j9iricwv5" />
    </div>;
}